/**
 * Procedural audio (WebAudio, no files): crowd bed driven by match intensity,
 * goal roar + chant, groans, referee whistles, ball kicks, and OB-truck UI sounds.
 *
 *   const audio = createAudio();
 *   button.onclick = () => audio.resume();     // must follow a user gesture
 *   audio.update(snapshot, newEvents, dt);     // every frame
 *   audio.ui('cut');
 *
 * CPU: one looping noise buffer shared by everything; crowd bed is 2 sources
 * + 3 filters. One-shots are short-lived node chains, capped in number.
 */
import type { MatchEvent, WorldSnapshot } from '../core/contracts';
import type { Mixer } from '../director/mixer';

export type UiSound = 'cut' | 'tick' | 'whoosh' | 'good' | 'bad' | 'chime';
export type WhistleKind = 'short' | 'long' | 'full_time';

export interface AudioEngine {
  /** Create/resume the AudioContext. Call from a user gesture. */
  resume(): Promise<void>;
  setMaster(v: number): void;
  /** Separate crowd/sfx and UI levels (0..1). */
  setLevels(levels: { crowd?: number; sfx?: number; ui?: number }): void;
  update(snapshot: WorldSnapshot, newEvents: readonly MatchEvent[], dt: number): void;
  ui(name: UiSound): void;
  whistle(kind: WhistleKind): void;
  /** Duck/mute the crowd (e.g. menus, pause). */
  setPaused(paused: boolean): void;
  /** Crowd bed ducked (menus / pause / half-time). */
  readonly paused: boolean;
  readonly ready: boolean;
  /** Live MediaStream of the mixed output (for video export), or null before resume(). */
  captureStream(): MediaStream | null;
  dispose(): void;
}

const MAX_VOICES = 24;

export function createAudio(): AudioEngine {
  let ctx: AudioContext | null = null;
  let out: DynamicsCompressorNode | null = null;
  let tap: MediaStreamAudioDestinationNode | null = null;
  let master!: GainNode, crowdBus!: GainNode, sfxBus!: GainNode, uiBus!: GainNode;
  let noise!: AudioBuffer;
  let rumbleG!: GainNode, murmurG!: GainNode, cheerG!: GainNode, cheerF!: BiquadFilterNode;
  const bedSources: AudioScheduledSourceNode[] = [];
  let masterLevel = 0.8;
  const levels = { crowd: 0.9, sfx: 0.9, ui: 0.7 };
  let paused = false;
  let voices = 0;
  let excitement = 0;       // decaying swell 0..1 from events
  let smoothIntensity = 0.2;
  let paramTimer = 0;
  let lastKickAt = 0;

  // -------------------------------------------------------------------- setup
  function makeNoise(c: AudioContext): AudioBuffer {
    const len = Math.floor(c.sampleRate * 4);
    const buf = c.createBuffer(2, len, c.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      // Paul Kellet's economy pink noise
      let b0 = 0, b1 = 0, b2 = 0;
      for (let i = 0; i < len; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99765 * b0 + w * 0.099046;
        b1 = 0.963 * b1 + w * 0.2965164;
        b2 = 0.57 * b2 + w * 1.0526913;
        d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.2;
      }
    }
    return buf;
  }

  function init(): AudioContext {
    const c = new AudioContext({ latencyHint: 'interactive' });
    ctx = c;
    noise = makeNoise(c);
    const comp = c.createDynamicsCompressor();
    comp.threshold.value = -14; comp.ratio.value = 4; comp.attack.value = 0.01; comp.release.value = 0.25;
    master = c.createGain(); master.gain.value = masterLevel;
    master.connect(comp).connect(c.destination);
    out = comp;
    crowdBus = c.createGain(); crowdBus.gain.value = levels.crowd; crowdBus.connect(master);
    sfxBus = c.createGain(); sfxBus.gain.value = levels.sfx; sfxBus.connect(master);
    uiBus = c.createGain(); uiBus.gain.value = levels.ui; uiBus.connect(master);

    // Crowd bed: two decorrelated loops of the stereo noise buffer.
    const bedIn = c.createGain(); bedIn.gain.value = 1;
    for (const [offset, rate] of [[0, 1], [1.7, 0.93]] as const) {
      const s = c.createBufferSource();
      s.buffer = noise; s.loop = true; s.playbackRate.value = rate;
      s.connect(bedIn); s.start(0, offset);
      bedSources.push(s);
    }
    const rumbleF = c.createBiquadFilter(); rumbleF.type = 'lowpass'; rumbleF.frequency.value = 380;
    const murmurF = c.createBiquadFilter(); murmurF.type = 'bandpass'; murmurF.frequency.value = 850; murmurF.Q.value = 0.8;
    cheerF = c.createBiquadFilter(); cheerF.type = 'bandpass'; cheerF.frequency.value = 1500; cheerF.Q.value = 0.9;
    rumbleG = c.createGain(); rumbleG.gain.value = 0.35;
    murmurG = c.createGain(); murmurG.gain.value = 0.3;
    cheerG = c.createGain(); cheerG.gain.value = 0.02;
    bedIn.connect(rumbleF).connect(rumbleG).connect(crowdBus);
    bedIn.connect(murmurF).connect(murmurG).connect(crowdBus);
    bedIn.connect(cheerF).connect(cheerG).connect(crowdBus);
    // Slow "breathing" of the murmur so the bed never sounds static.
    const lfo = c.createOscillator(); lfo.frequency.value = 0.17;
    const lfoDepth = c.createGain(); lfoDepth.gain.value = 0.08;
    lfo.connect(lfoDepth).connect(murmurG.gain); lfo.start();
    bedSources.push(lfo);
    return c;
  }

  // ---------------------------------------------------------------- helpers
  const now = (): number => ctx!.currentTime;

  function voice(dur: number): boolean {
    if (!ctx || ctx.state !== 'running' || voices >= MAX_VOICES) return false;
    voices++;
    window.setTimeout(() => { voices--; }, (dur + 0.1) * 1000);
    return true;
  }

  function noiseSrc(t: number, dur: number, rate = 1): AudioBufferSourceNode {
    const s = ctx!.createBufferSource();
    s.buffer = noise; s.loop = true; s.playbackRate.value = rate;
    s.start(t, Math.random() * 3);
    s.stop(t + dur + 0.05);
    return s;
  }

  function env(g: GainParam, t: number, peak: number, attack: number, hold: number, release: number): void {
    g.setValueAtTime(0.0001, t);
    g.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
    g.setValueAtTime(Math.max(0.0002, peak), t + attack + hold);
    g.exponentialRampToValueAtTime(0.0001, t + attack + hold + release);
  }
  type GainParam = AudioParam;

  function osc(type: OscillatorType, freq: number, t: number, dur: number): OscillatorNode {
    const o = ctx!.createOscillator();
    o.type = type; o.frequency.setValueAtTime(freq, t);
    o.start(t); o.stop(t + dur + 0.05);
    return o;
  }

  function gain(v = 0): GainNode { const g = ctx!.createGain(); g.gain.value = v; return g; }
  function filt(type: BiquadFilterType, f: number, q = 1): BiquadFilterNode {
    const b = ctx!.createBiquadFilter(); b.type = type; b.frequency.value = f; b.Q.value = q; return b;
  }

  // ------------------------------------------------------------------ crowd
  function roar(big: boolean): void {
    const dur = big ? 9 : 4;
    if (!voice(dur)) return;
    const t = now();
    const s = noiseSrc(t, dur, 1.05);
    const f = filt('bandpass', 700, 0.6);
    f.frequency.setValueAtTime(600, t);
    f.frequency.exponentialRampToValueAtTime(big ? 1700 : 1200, t + 0.6);
    f.frequency.exponentialRampToValueAtTime(900, t + dur);
    const g = gain();
    env(g.gain, t, big ? 1.3 : 0.6, 0.35, big ? 2.8 : 1, big ? 5.5 : 2.6);
    s.connect(f).connect(g).connect(crowdBus);
    // Low body
    const s2 = noiseSrc(t, dur, 0.8);
    const f2 = filt('lowpass', 500);
    const g2 = gain();
    env(g2.gain, t, big ? 0.9 : 0.4, 0.25, big ? 3 : 1, big ? 5 : 2.4);
    s2.connect(f2).connect(g2).connect(crowdBus);
    excitement = Math.max(excitement, big ? 1 : 0.6);
    if (big) chant(t + 3.2);
  }

  /** Rhythmic formant-filtered swell: "oh-oh-oh-oh" terrace chant feel. */
  function chant(t: number): void {
    const dur = 7;
    if (!voice(dur + 3.2)) return;
    const s = noiseSrc(t, dur, 1);
    const f1 = filt('bandpass', 520, 5);
    const f2 = filt('bandpass', 880, 6);
    const g = gain();
    const beat = 0.42;
    g.gain.setValueAtTime(0.0001, t);
    const n = Math.floor(dur / beat);
    for (let i = 0; i < n; i++) {
      const bt = t + i * beat;
      const accent = i % 4 === 0 ? 1 : i % 2 === 0 ? 0.75 : 0.55;
      const fade = Math.min(1, i / 3) * Math.min(1, (n - i) / 5);
      g.gain.setValueAtTime(0.0001, bt);
      g.gain.exponentialRampToValueAtTime(0.0002 + 0.9 * accent * fade, bt + 0.08);
      g.gain.exponentialRampToValueAtTime(0.0001 + 0.12 * fade, bt + beat * 0.85);
      // vowel movement
      f1.frequency.setValueAtTime(i % 2 ? 480 : 600, bt);
    }
    s.connect(f1).connect(g);
    s.connect(f2).connect(g);
    g.connect(crowdBus);
  }

  function groan(big: boolean): void {
    const dur = big ? 2.4 : 1.8;
    if (!voice(dur)) return;
    const t = now();
    const s = noiseSrc(t, dur, 0.95);
    const f = filt('bandpass', 750, 2.2);
    f.frequency.setValueAtTime(620, t);
    f.frequency.linearRampToValueAtTime(820, t + 0.35);     // rising "ooo"
    f.frequency.exponentialRampToValueAtTime(260, t + dur);   // falling "...ohh"
    const g = gain();
    env(g.gain, t, big ? 1.1 : 0.75, 0.25, 0.35, dur - 0.6);
    s.connect(f).connect(g).connect(crowdBus);
    excitement = Math.max(excitement, 0.45);
  }

  function ooh(): void {
    const dur = 1.4;
    if (!voice(dur)) return;
    const t = now();
    const s = noiseSrc(t, dur, 1);
    const f = filt('bandpass', 500, 3);
    f.frequency.setValueAtTime(450, t);
    f.frequency.exponentialRampToValueAtTime(1000, t + 0.5);
    f.frequency.exponentialRampToValueAtTime(700, t + dur);
    const g = gain();
    env(g.gain, t, 0.6, 0.2, 0.3, 0.9);
    s.connect(f).connect(g).connect(crowdBus);
    excitement = Math.max(excitement, 0.4);
  }

  // ------------------------------------------------------------------- sfx
  function whistleBlast(t: number, len: number, level = 0.32): void {
    const o = osc('sine', 2950, t, len);
    const warble = osc('square', 38, t, len);
    const wd = gain(140);
    warble.connect(wd).connect(o.frequency);
    const breath = noiseSrc(t, len, 1.4);
    const bf = filt('bandpass', 3000, 4);
    const bg = gain(0.18);
    breath.connect(bf).connect(bg);
    const g = gain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(level, t + 0.02);
    g.gain.setValueAtTime(level, t + len - 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
    const lp = filt('lowpass', 6500, 0.7);
    o.connect(g); bg.connect(g);
    g.connect(lp).connect(sfxBus);
  }

  function whistle(kind: WhistleKind): void {
    if (!voice(3)) return;
    const t = now() + 0.01;
    if (kind === 'short') whistleBlast(t, 0.22);
    else if (kind === 'long') { whistleBlast(t, 0.3); whistleBlast(t + 0.42, 1.0); }
    else { whistleBlast(t, 0.28); whistleBlast(t + 0.4, 0.28); whistleBlast(t + 0.8, 1.3); }
  }

  function kick(strength: number): void {
    if (!ctx) return;
    const t = now();
    if (t - lastKickAt < 0.07) return;
    lastKickAt = t;
    if (!voice(0.25)) return;
    const o = osc('sine', 150, t, 0.2);
    o.frequency.exponentialRampToValueAtTime(48, t + 0.12);
    const g = gain();
    env(g.gain, t, 0.55 * strength, 0.004, 0.01, 0.14);
    o.connect(g).connect(sfxBus);
    const n = noiseSrc(t, 0.03, 1);
    const hp = filt('highpass', 1800);
    const ng = gain();
    env(ng.gain, t, 0.25 * strength, 0.001, 0.004, 0.02);
    n.connect(hp).connect(ng).connect(sfxBus);
  }

  // -------------------------------------------------------------------- UI
  function ui(name: UiSound): void {
    if (!ctx || ctx.state !== 'running') return;
    const t = now();
    switch (name) {
      case 'cut': { // chunky switcher button: click + thunk
        if (!voice(0.12)) return;
        const n = noiseSrc(t, 0.02, 1);
        const bp = filt('bandpass', 3800, 1.4);
        const g = gain(); env(g.gain, t, 0.5, 0.001, 0.003, 0.018);
        n.connect(bp).connect(g).connect(uiBus);
        const o = osc('sine', 110, t, 0.08);
        o.frequency.exponentialRampToValueAtTime(60, t + 0.06);
        const og = gain(); env(og.gain, t, 0.45, 0.002, 0.01, 0.06);
        o.connect(og).connect(uiBus);
        break;
      }
      case 'tick': {
        if (!voice(0.06)) return;
        const o = osc('sine', 2200, t, 0.03);
        const g = gain(); env(g.gain, t, 0.12, 0.001, 0.004, 0.025);
        o.connect(g).connect(uiBus);
        break;
      }
      case 'whoosh': {
        if (!voice(0.7)) return;
        const n = noiseSrc(t, 0.65, 1);
        const bp = filt('bandpass', 300, 1.6);
        bp.frequency.setValueAtTime(260, t);
        bp.frequency.exponentialRampToValueAtTime(3600, t + 0.28);
        bp.frequency.exponentialRampToValueAtTime(500, t + 0.6);
        const g = gain(); env(g.gain, t, 0.55, 0.22, 0.04, 0.34);
        n.connect(bp).connect(g).connect(uiBus);
        break;
      }
      case 'good': {
        if (!voice(0.4)) return;
        [880, 1318.5].forEach((f, i) => {
          const tt = t + i * 0.07;
          const o = osc('triangle', f, tt, 0.25);
          const g = gain(); env(g.gain, tt, 0.18, 0.005, 0.03, 0.2);
          o.connect(g).connect(uiBus);
        });
        break;
      }
      case 'bad': {
        if (!voice(0.4)) return;
        const o = osc('sawtooth', 220, t, 0.3);
        o.frequency.exponentialRampToValueAtTime(140, t + 0.25);
        const lp = filt('lowpass', 900);
        const g = gain(); env(g.gain, t, 0.16, 0.005, 0.08, 0.18);
        o.connect(lp).connect(g).connect(uiBus);
        break;
      }
      case 'chime': {
        if (!voice(1.4)) return;
        [[1046.5, 0.16], [1568, 0.08], [2093, 0.05]].forEach(([f, a], i) => {
          const tt = t + (i === 0 ? 0 : 0.002);
          const o = osc('sine', f!, tt, 1.3);
          const g = gain(); env(g.gain, tt, a!, 0.003, 0.02, 1.2);
          o.connect(g).connect(uiBus);
        });
        const o2 = osc('sine', 1318.5, t + 0.14, 1.1);
        const g2 = gain(); env(g2.gain, t + 0.14, 0.12, 0.003, 0.02, 1);
        o2.connect(g2).connect(uiBus);
        break;
      }
    }
  }

  // ---------------------------------------------------------------- update
  function onEvent(e: MatchEvent): void {
    switch (e.type) {
      case 'goal': roar(true); break;
      case 'near_miss': groan(false); break;
      case 'woodwork': groan(true); break;
      case 'save': ooh(); break;
      case 'shot': case 'shot_on_target': kick(1); excitement = Math.max(excitement, 0.35); break;
      case 'pass': case 'dribble': kick(0.35); break;
      case 'long_ball': case 'cross': case 'goal_kick': case 'free_kick': case 'corner': kick(0.6); break;
      case 'foul': case 'offside': case 'yellow_card': case 'red_card': case 'penalty': whistle('short'); break;
      case 'kickoff': whistle('short'); break;
      case 'half_time': whistle('long'); break;
      case 'full_time': whistle('full_time'); roar(false); break;
      case 'counter_attack': case 'crowd_moment': excitement = Math.max(excitement, 0.4); break;
      default: break;
    }
  }

  const engine: AudioEngine = {
    get ready() { return !!ctx && ctx.state === 'running'; },
    get paused() { return paused; },
    async resume() {
      const c = ctx ?? init();
      if (c.state !== 'running') await c.resume();
    },
    setMaster(v) {
      masterLevel = Math.max(0, Math.min(1, v));
      if (ctx) master.gain.setTargetAtTime(masterLevel, now(), 0.05);
    },
    setLevels(l) {
      Object.assign(levels, l);
      if (!ctx) return;
      crowdBus.gain.setTargetAtTime(paused ? 0 : levels.crowd, now(), 0.1);
      sfxBus.gain.setTargetAtTime(levels.sfx, now(), 0.05);
      uiBus.gain.setTargetAtTime(levels.ui, now(), 0.05);
    },
    setPaused(p) {
      paused = p;
      if (ctx) crowdBus.gain.setTargetAtTime(p ? 0 : levels.crowd, now(), 0.3);
    },
    update(snapshot, newEvents, dt) {
      if (!ctx || ctx.state !== 'running') return;
      for (const e of newEvents) onEvent(e);
      excitement = Math.max(0, excitement - dt * 0.12);
      smoothIntensity += (snapshot.intensity - smoothIntensity) * Math.min(1, dt * 1.5);
      paramTimer -= dt;
      if (paramTimer > 0) return;
      paramTimer = 0.1; // retarget the bed at 10Hz; setTargetAtTime does the smoothing
      const live = snapshot.phase === 'half_time' || snapshot.phase === 'full_time' ? 0.4 : 1;
      const i = Math.min(1, smoothIntensity * 0.8 + excitement * 0.7);
      const t = now();
      rumbleG.gain.setTargetAtTime((0.28 + 0.25 * i) * live, t, 0.4);
      murmurG.gain.setTargetAtTime((0.22 + 0.35 * i) * live, t, 0.4);
      cheerG.gain.setTargetAtTime((0.01 + 0.45 * i * i) * live, t, 0.3);
      cheerF.frequency.setTargetAtTime(1200 + 900 * i, t, 0.5);
    },
    ui,
    whistle,
    captureStream() {
      if (!ctx || !out) return null;
      if (!tap) { tap = ctx.createMediaStreamDestination(); out.connect(tap); }
      return tap.stream;
    },
    dispose() {
      for (const s of bedSources) { try { s.stop(); } catch { /* already stopped */ } }
      void ctx?.close();
      ctx = null; out = null; tap = null;
    },
  };
  return engine;
}

/** Wire standard truck UI sounds to mixer events. Returns a disposer. */
export function bindMixerSounds(mixer: Mixer, audio: AudioEngine): () => void {
  const offs = [
    mixer.on('cut', (c) => { if (c.transition === 'cut') audio.ui('cut'); }),
    mixer.on('transitionStart', (t) => audio.ui(t.kind === 'stinger' ? 'whoosh' : 'cut')),
    mixer.on('graphicShown', (g) => { if (g.kind !== 'scorebug') audio.ui('tick'); }),
  ];
  let lastPvw = mixer.state.previewCamId;
  offs.push(mixer.on('change', (s) => { if (s.previewCamId !== lastPvw) { lastPvw = s.previewCamId; audio.ui('tick'); } }));
  return () => offs.forEach((o) => o());
}
